import React, { PureComponent } from "react";
import $ from "jquery";
import classnames from "classnames";

import { numberFormat } from "~/core/scripts/utils";
import Icon from "~/core/scripts/components/Icon";
import ChartWrapper from "~/core/scripts/chart-components/ChartWrapper";
import * as d3Dsv from "d3-dsv";
import d3 from "~/kits/general/scripts/d3";
import { getDateOfISOWeek, getData } from "../utils";
import { extendData } from "./multicharts-utils";

const startYear = 2008;
const endYear = 2021;
const startWeek = 40;
const endWeek = 20;
const chartHeight = 35;
const gradeLabelWidth = 110;

const deathColors = ["#ffdca1", "#f1926c", "#cb4964", "#821465"];
const csv = d3Dsv.dsvFormat(";");

function toDate(dateStr) {
  const splittedDate = dateStr.split(".");

  return new Date(splittedDate[2], +splittedDate[0] - 1, splittedDate[1]);
}

function getBorderColor(deathsEstStr) {
  const deathsEst = +deathsEstStr;
  let cat = 0;

  if (deathsEst === 1) {
    cat = 0;
  } else if (deathsEst === 2) {
    cat = 1;
  } else if (deathsEst === 3) {
    cat = 2;
  } else if (deathsEst === 4) {
    cat = 3;
  }

  return deathColors[cat];
}

const seasonData = d3
  .range(startYear, endYear)
  .reduce((res, year) => {
    return res.concat([
      {
        startYear: year,
        endYear: year + 1,
        data: [],
        season: `${year}/${(year + 1).toString().substr(2, 4)}`,
      },
    ]);
  }, [])
  .reverse();

class MultiCharts extends PureComponent {
  state = {
    isLoading: true,
    numberFormat: {},
    activeCardIndex: 0,
  };

  componentDidMount() {
    this.chartWidth = Math.min(
      this.container.getBoundingClientRect().width - gradeLabelWidth,
      450
    );

    const startDate = new Date(2017, 9, 1);
    const endDate = new Date(2018, 4, 20);

    this.xScale = d3
      .scaleTime()
      .domain([startDate, endDate])
      .range([0, this.chartWidth]);

    $.when(getData(), $.get("data/linechart.csv")).then((data, csvString) => {
      const parsedCsv = csv.parse(csvString[0]);
      this.initWidget(data[0], parsedCsv);
    });
  }

  initWidget(dataRaw, csvData) {
    // group data by season
    const data = dataRaw
      .reduce((res, item, index) => {
        item.date = getDateOfISOWeek(item.week, item.year);
        return res.map((d) => {
          const isStartYear = d.startYear === item.year;
          const isEndYear = d.endYear === item.year;
          const isStartWeek = isStartYear && item.week >= startWeek;
          const isEndWeek = isEndYear && item.week <= endWeek;
          const isMatching = isStartWeek || isEndWeek;

          return {
            ...d,
            data: isMatching ? [...d.data, item] : d.data,
          };
        });
      }, seasonData)
      .map(extendData(this.chartWidth, chartHeight));

    this.setState({
      data,
      csvData,
      isLoading: false,
    });
  }

  onCardClick(index) {
    this.setState((prevState) => ({
      activeCardIndex: prevState.activeCardIndex === index ? null : index,
    }));
  }

  renderCard(item, index) {
    const isActive = index === this.state.activeCardIndex;
    const maxYDataItem = item.data.find((d) => d.abs === item.yMax);
    const itemCsvData = this.state.csvData.find(
      (d) => d.season === item.season
    );
    const seasonStartX = item.xScale(toDate(itemCsvData.startweek));
    const seasonEndX = item.xScale(toDate(itemCsvData.endweek));
    const newYearX = item.xScale(new Date(item.endYear, 0, 1));
    const cardClasses = classnames("multicharts__card", {
      "is-active": isActive,
    });
    const deathsCategoryColor = getBorderColor(itemCsvData.index);

    return (
      <div
        className={cardClasses}
        key={`card__${item.season}`}
        onClick={() => this.onCardClick(index)}
        style={{ borderLeftColor: deathsCategoryColor }}
      >
        <div className="multicharts__card-content">
          <div className="multicharts__card-season">
            <div className="multicharts__card-season-label">{item.season}</div>
            <Icon type="down" />
          </div>
          <ChartWrapper width={this.chartWidth} height={chartHeight}>
            <rect
              className="season-bar"
              x={seasonStartX}
              width={seasonEndX - seasonStartX}
              height={chartHeight}
              y={0}
              rx={2}
              ry={2}
            />
            <line
              x1={newYearX}
              x2={newYearX}
              y2={chartHeight}
              className="year-line"
            />
            <path
              className="chart__line"
              d={item.line(item.data)}
              stroke="#333"
            />
            <circle
              className="chart__circle"
              cx={item.xScale(maxYDataItem.date)}
              cy={item.yScale(maxYDataItem.abs)}
              r={3}
            />
          </ChartWrapper>
        </div>
        {isActive && (
          <div className="multicharts__card-extend">
            <div className="multicharts__card-row">
              <div className="multicharts__card-col deaths-est">
                <div className="multicharts__card-label">
                  geschätzte Todesfälle
                </div>
                <div className="multicharts__card-value">
                  <div
                    className="deaths-circle"
                    style={{ background: deathsCategoryColor }}
                  />
                  {itemCsvData.deaths_est === "0"
                    ? "wenige"
                    : numberFormat(itemCsvData.deaths_est)}
                </div>
              </div>
              <div className="multicharts__card-col">
                <div className="multicharts__card-label">gemeldete Fälle</div>
                <div className="multicharts__card-value">
                  {numberFormat(itemCsvData.cases)}
                </div>
              </div>
            </div>

            <div className="multicharts__card-row">
              <div className="multicharts__card-col">
                <div className="multicharts__card-label">
                  gemeldete Todesfälle
                </div>
                <div className="multicharts__card-value">
                  {numberFormat(itemCsvData.deaths_conf)}
                </div>
              </div>
              <div className="multicharts__card-col">
                <div className="multicharts__card-label">
                  Dauer der Grippewelle
                </div>
                <div className="multicharts__card-value">
                  {numberFormat(itemCsvData.weeks)} Wochen
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderXAxis() {
    if (this.state.isLoading) {
      return null;
    }

    const labels = [
      {
        label: "Okt.",
        date: new Date(2017, 9, 1),
      },
      {
        label: "Dez.",
        date: new Date(2017, 11, 1),
      },
      {
        label: "Jan.",
        date: new Date(2018, 0, 1),
      },
      {
        label: "März",
        date: new Date(2018, 2, 1),
      },
      {
        label: "Mai",
        date: new Date(2018, 4, 1),
      },
    ];

    return (
      <div className="multicharts__xaxis">
        <div
          className="multicharts__xaxis-grade"
          style={{
            width: `${gradeLabelWidth}px`,
            minWidth: `${gradeLabelWidth}px`,
          }}
        >
          Schweregrad
        </div>
        <div className="multicharts__xaxis-labels">
          {labels.map((d) => (
            <div
              key={`xaxis-${d.label}`}
              className="multicharts__xaxis-label"
              style={{ left: `${this.xScale(d.date)}px` }}
            >
              {d.label}
            </div>
          ))}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div className="multicharts" ref={(ref) => (this.container = ref)}>
        <div className="multicharts__header">
          <div
            style={{
              width: `${gradeLabelWidth}px`,
              minWidth: `${gradeLabelWidth}px`,
            }}
            className="multicharts__header-season"
          >
            Saison
          </div>
          <div className="multicharts__header-cases">Fälle im Verlauf</div>
          <div className="multicharts__header-flu">Grippewelle</div>
        </div>
        {this.renderXAxis()}
        {!this.state.isLoading && (
          <div className="multicharts__cards">
            {this.state.data.map((d, i) => this.renderCard(d, i))}
          </div>
        )}
        <div className="multicharts__footer">
          <div className="multicharts__footer-season">
            <div className="multicharts__footer-season-title">
              Schwere der Grippe Saison
            </div>
            <div className="multicharts__footer-season-legend">
              <div>leicht</div>
              <div className="multicharts__footer-season-items">
                {deathColors.map((c) => (
                  <div key={`legend-${c}`} style={{ background: c }} />
                ))}
              </div>
              <div>schwer</div>
            </div>
          </div>
          <div className="multicharts__footer-max">
            <strong>Höhepunkt</strong> der Saison
          </div>
        </div>
      </div>
    );
  }
}

export default MultiCharts;
